import {
  useMediaQuery,
  WhmAlert,
  WhmAutocomplete,
  WhmBox,
  WhmButton,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmDivider,
  WhmDropzone,
  WhmGrid,
  WhmIconButton,
  WhmInputAdornment,
  WhmLoadingButton,
  WhmMenuItem,
  WhmStack,
  WhmTextField,
  whmTheme,
  WhmTooltip,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import { I18nContext, translationKeys } from "../../../../contexts/I18nContext";
import { ReactComponent as CloseIcon } from "@securitize/assets-sec-ui/dist/icons/svg/exit.svg";
import { Form, Formik, useFormikContext } from "formik";
import * as yup from "yup";
import { useContext, useEffect, useId, useRef, useState } from "react";
import CountryTextField from "../../../shared/CountryTextField/CountryTextField";
import { ReactComponent as OpenEye } from "@securitize/assets-sec-ui/dist/icons/svg/icon-eye-show.svg";
import { ReactComponent as ClosedEye } from "@securitize/assets-sec-ui/dist/icons/svg/icon-eye-hide.svg";
import { ReactComponent as InfoIcon } from "@securitize/assets-sec-ui/dist/icons/svg/info.svg";
import FormikWhmTextField from "../../../shared/FormikWhmTextField";
import moment from "moment";
import { updateKeyParty } from "../../../../data/api";
import { useQueryClient } from "@tanstack/react-query";
import { SnackbarContext } from "../../../shared/Snackbar";
import ErrorSnackbar from "../../../shared/ErrorSnackbar";
import { ResponseError } from "../../../../data/types/responseError";
import { UboOwning } from "../../../../data/dtos/create-key-party.dto";
import DatePickerInput from "../../../shared/DatePickerInput";
import DriversLicenseFrontViewSampleDialog from "./DriversLicenseFrontViewSampleDialog";
import DriversLicenseBackViewSampleDialog from "./DriversLicenseBackViewSampleDialog";
import PassportViewSampleDialog from "./PassportViewSampleDialog";
import { countryDoesNotRequireTaxId } from "./utils";
import {
  allowedStreetCityCharsRegex,
  allowedTaxIdCharsRegex,
  allowedZipCodeCharsRegex,
} from "../../../../utils/validations";
import { getSensitiveFieldMaskProps } from "../../../../utils/maskUtils";
import { ReactComponent as CheckMark } from "@securitize/assets-sec-ui/dist/icons/svg/check.svg";
import { statesUS } from "../../../Registration/countriesList";
import { KeyParty } from "../../../../data/dtos/get-key-party.dto";
import {
  useGetKeyPartyDocumentsQuery,
  useUploadInvestorDocumentTempMutation,
  useAddKeyPartyDocumentMutation,
  useDeleteKeyPartyDocumentMutation,
} from "../../../../data/useProfile";
import {
  DocumentState,
  DocumentStatuses,
  TempDocument,
} from "../../documents/interfaces/documents-interfaces";
import {
  KEY_PARTY_DOCUMENT_TYPES,
  KEY_PARTY_ID_TYPES,
  KEY_PARTY_UPLOADER_KEYS,
} from "../constants";
import { invalidateKeyPartiesQueries } from "../../../../utils/queryInvalidation";

function FixIdType() {
  const formikContext = useFormikContext<{
    keyParty: Omit<KeyParty, "id">;
    idType: string;
  }>();

  useEffect(() => {
    if (formikContext.values.keyParty.address?.countryCode !== "US") {
      void formikContext.setFieldValue("idType", KEY_PARTY_ID_TYPES.PASSPORT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- False positive.
  }, [formikContext.values.keyParty.address?.countryCode]);

  return null;
}

function PrefillIdType({ documents }: { documents: DocumentState[] }) {
  const formikContext = useFormikContext<{
    keyParty: Omit<KeyParty, "id">;
    idType: string;
  }>();

  const hasUploadedPassport = documents.some(
    (d) =>
      d.documentType === KEY_PARTY_UPLOADER_KEYS.PASSPORT &&
      d.status === DocumentStatuses.UPLOADED,
  );
  const hasUploadedDriversLicense = documents.some(
    (d) =>
      (d.documentType === KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_FRONT ||
        d.documentType === KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_BACK) &&
      d.status === DocumentStatuses.UPLOADED,
  );

  useEffect(() => {
    if (
      formikContext.values.keyParty.address?.countryCode === "US" &&
      hasUploadedDriversLicense &&
      !hasUploadedPassport
    ) {
      void formikContext.setFieldValue(
        "idType",
        KEY_PARTY_ID_TYPES.DRIVERS_LICENSE,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- False positive.
  }, [
    hasUploadedPassport,
    hasUploadedDriversLicense,
    formikContext.values.keyParty.address?.countryCode,
  ]);

  return null;
}

export default function CompleteIndividualDialog({
  open,
  onClose,
  keyParty,
  readOnly = false,
  flaggedComment,
}: {
  open: boolean;
  keyParty?: KeyParty;
  onClose: () => void;
  readOnly?: boolean;
  flaggedComment?: string;
}) {
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));
  const addIndividualFormId = useId();
  const [displayTopDivider, setDisplayTopDivider] = useState(false);
  const [displayBottomDivider, setDisplayBottomDivider] = useState(false);
  const { getTranslation, language } = useContext(I18nContext);
  const [hideTaxId, setHideTaxId] = useState(true);
  const taxIdMaskProps = getSensitiveFieldMaskProps(hideTaxId);
  const [passportViewSampleDialogOpen, setPassportViewSampleDialogOpen] =
    useState(false);
  const [
    driverLicenseFrontViewSampleDialogOpen,
    setDriverLicenseFrontViewSampleDialogOpen,
  ] = useState(false);
  const [
    driverLicenseBackViewSampleDialogOpen,
    setDriverLicenseBackViewSampleDialogOpen,
  ] = useState(false);

  const dialogContentRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const snackbarContext = useContext(SnackbarContext);
  const addKeyPartyDocumentMutation = useAddKeyPartyDocumentMutation();
  const uploadInvestorDocumentTempMutation =
    useUploadInvestorDocumentTempMutation();
  const deleteKeyPartyDocumentMutation = useDeleteKeyPartyDocumentMutation();
  const keyPartyDocumentsQuery = useGetKeyPartyDocumentsQuery(keyParty?.id);
  const [tempDocuments, setTempDocuments] = useState<Map<string, TempDocument>>(
    new Map(),
  );
  const [documents, setDocuments] = useState<DocumentState[]>([]);

  const handleClose = () => {
    setTempDocuments(new Map());
    setDocuments([]);
    onClose();
  };

  useEffect(() => {
    if (open && keyPartyDocumentsQuery.data) {
      const documentTypes = [
        KEY_PARTY_UPLOADER_KEYS.PASSPORT,
        KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_FRONT,
        KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_BACK,
      ];
      const initialDocuments: DocumentState[] = documentTypes.map(
        (docType, index) => {
          let existingDoc;
          if (docType === KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_FRONT) {
            existingDoc = keyPartyDocumentsQuery.data.data.find(
              (d) =>
                d.documentType === KEY_PARTY_DOCUMENT_TYPES.DRIVING_LICENCE &&
                d.side === "front",
            );
          } else if (docType === KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_BACK) {
            existingDoc = keyPartyDocumentsQuery.data.data.find(
              (d) =>
                d.documentType === KEY_PARTY_DOCUMENT_TYPES.DRIVING_LICENCE &&
                d.side === "back",
            );
          } else {
            existingDoc = keyPartyDocumentsQuery.data.data.find(
              (d) => d.documentType === KEY_PARTY_DOCUMENT_TYPES.PASSPORT,
            );
          }

          if (existingDoc) {
            return {
              documentId: existingDoc.id,
              documentType: docType,
              fileName: existingDoc.fileName,
              fileUrl: existingDoc.url,
              thumbnail: existingDoc.thumbnail,
              index,
              status: DocumentStatuses.UPLOADED,
            };
          }

          return {
            documentType: docType,
            index,
            status: DocumentStatuses.PENDING,
          };
        },
      );
      setDocuments(initialDocuments);
    }
  }, [keyPartyDocumentsQuery.data, open]);

  const updateDocumentState = (documentState: DocumentState) => {
    setDocuments((prevDocuments) => {
      const updatedDocuments = [...prevDocuments];
      updatedDocuments[documentState.index] = documentState;
      return updatedDocuments;
    });
  };

  const uploadDocument = async (
    file: File,
    documentType: string,
    index: number,
  ) => {
    try {
      const tempUploadResponse =
        await uploadInvestorDocumentTempMutation.mutateAsync({ file });

      setTempDocuments((prev) => {
        const newMap = new Map(prev);
        newMap.set(documentType, {
          documentType,
          fileName: tempUploadResponse.fileName,
          fileType: tempUploadResponse.fileType,
          url: tempUploadResponse.url,
          file,
        });
        return newMap;
      });

      updateDocumentState({
        documentType,
        file,
        fileName: tempUploadResponse.fileName,
        index,
        status: DocumentStatuses.UPLOADED,
      });
    } catch (error) {
      snackbarContext.showSnackbar({
        severity: "error",
        message: <ErrorSnackbar error={error as ResponseError} />,
      });
    }
  };

  const removeDocument = async (
    documentType: string,
    index: number,
    documentId?: string,
  ) => {
    if (tempDocuments.has(documentType)) {
      setTempDocuments((prev) => {
        const newMap = new Map(prev);
        newMap.delete(documentType);
        return newMap;
      });
      updateDocumentState({
        documentType,
        index,
        status: DocumentStatuses.PENDING,
      });
      return;
    }

    if (!documentId) {
      throw new Error("Unreachable.");
    }

    await deleteKeyPartyDocumentMutation.mutateAsync(
      { keyPartyId: keyParty!.id, documentId },
      {
        onError: (error) => {
          snackbarContext.showSnackbar({
            severity: "error",
            message: <ErrorSnackbar error={error} />,
          });
        },
        onSuccess: () => {
          Promise.all([
            invalidateKeyPartiesQueries(queryClient),
            queryClient.invalidateQueries({
              queryKey: ["key-party-documents", keyParty!.id],
            }),
          ])
            .then(() => {
              updateDocumentState({
                documentType,
                index,
                status: DocumentStatuses.PENDING,
              });
            })
            .catch((error) => {
              snackbarContext.showSnackbar({
                severity: "error",
                message: <ErrorSnackbar error={error as ResponseError} />,
              });
            });
        },
      },
    );
  };

  return (
    <WhmDialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? "xl" : "md"}
      TransitionProps={{
        onEnter: () => {
          if (dialogContentRef.current) {
            if (
              dialogContentRef.current.scrollHeight >
              dialogContentRef.current.clientHeight
            ) {
              setDisplayBottomDivider(true);
            }
          }
        },
      }}
    >
      <>
        <Formik
          initialValues={{
            keyParty: {
              taxId: "",
              taxCountryCode: "",
              address: {
                countryCode: "",
                street: "",
                entrance: "",
                city: "",
                state: "",
                zip: "",
              },
              email: "",
              isLegalSigner: false,
              uboOwning: "none" as UboOwning,
              birthDate: null,
              ...keyParty,
            },
            idType: KEY_PARTY_ID_TYPES.PASSPORT as string,
          }}
          onSubmit={async (values, helpers) => {
            helpers.setSubmitting(true);

            try {
              // First, upload all temp documents to permanent storage
              for (const [documentType, tempDoc] of Array.from(
                tempDocuments.entries(),
              )) {
                let actualDocType: string = KEY_PARTY_DOCUMENT_TYPES.PASSPORT;
                let side = "front";

                if (
                  documentType === KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_FRONT
                ) {
                  actualDocType = KEY_PARTY_DOCUMENT_TYPES.DRIVING_LICENCE;
                  side = "front";
                } else if (
                  documentType === KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_BACK
                ) {
                  actualDocType = KEY_PARTY_DOCUMENT_TYPES.DRIVING_LICENCE;
                  side = "back";
                }

                await addKeyPartyDocumentMutation.mutateAsync({
                  keyPartyId: keyParty!.id,
                  documentType: actualDocType,
                  documentCategory: "signer-identification",
                  fileName: tempDoc.fileName,
                  fileType: tempDoc.fileType,
                  side,
                });
              }

              await updateKeyParty(keyParty!.id, values.keyParty);
              await Promise.all([
                invalidateKeyPartiesQueries(queryClient),
                queryClient.invalidateQueries({
                  queryKey: ["key-party-documents", keyParty!.id],
                }),
              ]);

              snackbarContext.showSnackbar({
                message: (
                  <WhmStack direction="row" alignItems="center" gap="16px">
                    <CheckMark fill="white" />
                    <Translate
                      translationKey={
                        translationKeys.PROFILE_KEY_PARTIES_UPDATE_SUCCESS
                      }
                      replacements={{
                        keyPartyName: keyParty?.displayName ?? "",
                      }}
                    />
                  </WhmStack>
                ),
                severity: "info",
              });

              handleClose();
            } catch (error) {
              snackbarContext.showSnackbar({
                message: <ErrorSnackbar error={error as ResponseError} />,
                severity: "error",
              });
            } finally {
              helpers.setSubmitting(false);
            }
          }}
          validationSchema={yup.object().shape({
            keyParty: yup.object().shape({
              taxCountryCode: yup.string(),
              taxId: yup.string().matches(allowedTaxIdCharsRegex, {
                message: getTranslation(
                  translationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_TAX_ID_INVALID,
                ),
              }),
              address: yup.object({
                // Nesting the first schema under "address"
                street: yup
                  .string()
                  .matches(
                    allowedStreetCityCharsRegex,
                    getTranslation(
                      translationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_STREET_INVALID,
                    ),
                  )
                  .trim(),
                entrance: yup
                  .string()
                  .transform((value: string) =>
                    value === "" ? undefined : value,
                  )
                  .matches(
                    allowedStreetCityCharsRegex,
                    getTranslation(
                      translationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_STREET_INVALID,
                    ),
                  )
                  .trim(),
                city: yup
                  .string()
                  .matches(
                    allowedStreetCityCharsRegex,
                    getTranslation(
                      translationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_CITY_INVALID,
                    ),
                  )
                  .trim(),
                zip: yup
                  .string()
                  .trim()
                  .matches(
                    allowedZipCodeCharsRegex,
                    getTranslation(
                      translationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ZIP_INVALID,
                    ),
                  ),
                countryCode: yup.string(),
                state: yup.string().nullable(),
              }),
              birthDate: yup
                .date()
                .typeError(
                  getTranslation(
                    translationKeys.PROFILE_KEY_PARTIES_BIRTH_DATE_AGE_ERROR,
                  ),
                )
                .nullable()
                .max(
                  new Date(
                    new Date().getFullYear() - 18,
                    new Date().getMonth(),
                    new Date().getDate(),
                  ),
                  getTranslation(
                    translationKeys.PROFILE_KEY_PARTIES_BIRTH_DATE_AGE_ERROR,
                  ),
                ),
            }),
          })}
        >
          {(formik) => (
            <>
              <FixIdType />
              <PrefillIdType documents={documents} />
              <WhmDialogTitle variant="h4">
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_COMPLETE_DETAILS_TITLE
                  }
                />
              </WhmDialogTitle>
              <WhmIconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  right: "8px",
                  top: "8px",
                }}
              >
                <CloseIcon />
              </WhmIconButton>
              {displayTopDivider && <WhmDivider />}
              <WhmDialogContent
                ref={dialogContentRef}
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, offsetHeight } =
                    e.currentTarget;

                  setDisplayTopDivider(scrollTop !== 0);
                  setDisplayBottomDivider(
                    scrollTop !== scrollHeight - offsetHeight,
                  );
                }}
              >
                <Form id={addIndividualFormId}>
                  <WhmStack gap="16px">
                    {flaggedComment && (
                      <WhmAlert icon={false} severity="warning">
                        {flaggedComment}
                      </WhmAlert>
                    )}
                    <WhmTypography>
                      <TranslateHtml
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_COMPLETE_DETAILS_SUBTITLE
                        }
                        replacements={{
                          name: keyParty?.displayName || "",
                        }}
                      />
                    </WhmTypography>
                    <WhmTypography variant="subtitle1">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_TAX_INFO_TITLE
                        }
                      />
                    </WhmTypography>
                    <WhmGrid
                      container
                      columns={{ xs: 6, md: 12 }}
                      spacing="16px"
                    >
                      <WhmGrid item xs={6}>
                        <CountryTextField
                          name="keyParty.taxCountryCode"
                          fullWidth
                          value={formik.values.keyParty.taxCountryCode}
                          label={
                            <Translate
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_TAX_RESIDENCE_COUNTRY
                              }
                            />
                          }
                          fieldError={
                            formik.touched.keyParty?.taxCountryCode &&
                            Boolean(formik.errors.keyParty?.taxCountryCode)
                          }
                          helperText={
                            formik.touched.keyParty?.taxCountryCode &&
                            formik.errors.keyParty?.taxCountryCode
                          }
                          onChange={(e, v) => {
                            void formik.setFieldValue(
                              "keyParty.taxCountryCode",
                              v?.value ?? "",
                            );
                          }}
                          onBlur={() => {
                            void formik.setFieldTouched(
                              "keyParty.taxCountryCode",
                            );
                          }}
                          disabled={readOnly || formik.isSubmitting}
                        />
                      </WhmGrid>
                      <WhmGrid item xs={6}>
                        <WhmStack direction="row" spacing="8px">
                          <FormikWhmTextField
                            sx={{ margin: 0, ...taxIdMaskProps.sx }}
                            label={
                              formik.values.keyParty.taxCountryCode === "US"
                                ? getTranslation(
                                    translationKeys.PROFILE_KEY_PARTIES_SSN,
                                  )
                                : getTranslation(
                                    translationKeys.PROFILE_KEY_PARTIES_TAX_ID,
                                  )
                            }
                            fullWidth
                            name="keyParty.taxId"
                            type={taxIdMaskProps.type}
                            autoComplete={taxIdMaskProps.autoComplete}
                            disabled={
                              readOnly ||
                              formik.isSubmitting ||
                              countryDoesNotRequireTaxId(
                                formik.values.keyParty.taxCountryCode,
                              )
                            }
                            helperText={
                              countryDoesNotRequireTaxId(
                                formik.values.keyParty.taxCountryCode,
                              )
                                ? getTranslation(
                                    translationKeys.PROFILE_KEY_PARTIES_TAX_NOT_REQUIRED,
                                  )
                                : formik.touched.keyParty?.taxId &&
                                  formik.errors.keyParty?.taxId
                            }
                            autoTrim
                            InputProps={{
                              endAdornment: countryDoesNotRequireTaxId(
                                formik.values.keyParty.taxCountryCode,
                              ) ? null : (
                                <WhmInputAdornment position="end">
                                  {hideTaxId ? (
                                    <WhmIconButton
                                      onClick={() => setHideTaxId(false)}
                                      size="small"
                                    >
                                      <ClosedEye
                                        width="20"
                                        height="20"
                                        fill={whmTheme.palette.action.active}
                                      />
                                    </WhmIconButton>
                                  ) : (
                                    <WhmIconButton
                                      onClick={() => setHideTaxId(true)}
                                      size="small"
                                    >
                                      <OpenEye
                                        width="20"
                                        height="20"
                                        fill={whmTheme.palette.action.active}
                                      />
                                    </WhmIconButton>
                                  )}
                                </WhmInputAdornment>
                              ),
                            }}
                          />
                          <WhmBox sx={{ paddingTop: "18px" }}>
                            <WhmTooltip
                              title={getTranslation(
                                translationKeys.PROFILE_KEY_PARTIES_TAX_PURPOSE_INFO,
                              )}
                            >
                              <InfoIcon
                                width="20"
                                height="20"
                                fill={whmTheme.palette.action.active}
                              />
                            </WhmTooltip>
                          </WhmBox>
                        </WhmStack>
                      </WhmGrid>
                    </WhmGrid>
                    <WhmDivider />
                    <WhmStack>
                      <WhmTypography variant="subtitle1">
                        <Translate
                          translationKey={
                            translationKeys.PROFILE_KEY_PARTIES_RESIDENTIAL_ADDRESS_TITLE
                          }
                        />
                      </WhmTypography>
                      <WhmTypography variant="body2" color="text.secondary">
                        <TranslateHtml
                          translationKey={
                            translationKeys.PROFILE_KEY_PARTIES_RESIDENTIAL_ADDRESS_SUBTITLE
                          }
                        />
                      </WhmTypography>
                    </WhmStack>
                    <WhmGrid
                      container
                      columns={{ xs: 6, md: 12 }}
                      spacing="16px"
                    >
                      <WhmGrid item xs={6}>
                        <FormikWhmTextField
                          sx={{ margin: 0 }}
                          label={
                            <Translate
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_ADDRESS_STREET
                              }
                            />
                          }
                          fullWidth
                          name="keyParty.address.street"
                          autoTrim
                          placeholder={getTranslation(
                            translationKeys.PROFILE_KEY_PARTIES_ADDRESS_STREET_PLACEHOLDER,
                          )}
                          disabled={readOnly || formik.isSubmitting}
                        />
                      </WhmGrid>
                      <WhmGrid item xs={6}>
                        <FormikWhmTextField
                          sx={{ margin: 0 }}
                          label={
                            <Translate
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_ADDRESS_ADDITIONAL_INFO
                              }
                            />
                          }
                          fullWidth
                          name="keyParty.address.entrance"
                          autoTrim
                          disabled={readOnly || formik.isSubmitting}
                        />
                      </WhmGrid>
                      <WhmGrid item xs={6}>
                        <FormikWhmTextField
                          sx={{ margin: 0 }}
                          label={
                            <Translate
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_ADDRESS_CITY
                              }
                            />
                          }
                          fullWidth
                          name="keyParty.address.city"
                          placeholder={getTranslation(
                            translationKeys.PROFILE_KEY_PARTIES_ADDRESS_CITY_PLACEHOLDER,
                          )}
                          disabled={readOnly || formik.isSubmitting}
                        />
                      </WhmGrid>
                      <WhmGrid item xs={6}>
                        <FormikWhmTextField
                          sx={{ margin: 0 }}
                          label={
                            <Translate
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_ADDRESS_ZIP
                              }
                            />
                          }
                          fullWidth
                          name="keyParty.address.zip"
                          placeholder={getTranslation(
                            translationKeys.PROFILE_KEY_PARTIES_ADDRESS_ZIP_PLACEHOLDER,
                          )}
                          disabled={readOnly || formik.isSubmitting}
                        />
                      </WhmGrid>
                      <WhmGrid item xs={6}>
                        <CountryTextField
                          label={
                            <Translate
                              translationKey={
                                translationKeys.REGISTRATION_RESIDENCE_COUNTRY
                              }
                            />
                          }
                          fullWidth
                          name="keyParty.address.countryCode"
                          value={formik.values.keyParty.address.countryCode}
                          fieldError={
                            formik.touched.keyParty?.address?.countryCode &&
                            Boolean(
                              formik.errors.keyParty?.address?.countryCode,
                            )
                          }
                          helperText={
                            formik.touched.keyParty?.address?.countryCode &&
                            formik.errors.keyParty?.address?.countryCode
                          }
                          onChange={(e, v) => {
                            void formik.setFieldValue(
                              "keyParty.address.countryCode",
                              v?.value ?? "",
                            );
                          }}
                          onBlur={() => {
                            void formik.setFieldTouched(
                              "keyParty.address.countryCode",
                            );
                          }}
                          disabled={readOnly || formik.isSubmitting}
                        />
                      </WhmGrid>
                      {formik.values.keyParty.address.countryCode === "US" && (
                        <WhmGrid item xs={6}>
                          <WhmAutocomplete
                            options={statesUS}
                            value={
                              statesUS.find(
                                (state) =>
                                  state.value ===
                                  formik.values.keyParty.address.state,
                              ) || null
                            }
                            fullWidth
                            disabled={readOnly || formik.isSubmitting}
                            onChange={(_, newValue) => {
                              void formik.setFieldValue(
                                "keyParty.address.state",
                                newValue ? newValue.value : null,
                              );
                            }}
                            onBlur={formik.handleBlur}
                            clearText={getTranslation(
                              translationKeys.AUTOCOMPLETE_CLEAR,
                            )}
                            openText={getTranslation(
                              translationKeys.AUTOCOMPLETE_OPEN,
                            )}
                            closeText={getTranslation(
                              translationKeys.AUTOCOMPLETE_CLOSE,
                            )}
                            renderInput={(props) => (
                              <WhmTextField
                                {...props}
                                error={
                                  formik.touched.keyParty?.address?.state &&
                                  Boolean(
                                    formik.errors.keyParty?.address?.state,
                                  )
                                }
                                placeholder={getTranslation(
                                  translationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_STATE_PLACEHOLDER,
                                )}
                                inputProps={{
                                  // eslint-disable-next-line react/prop-types
                                  ...props.inputProps,
                                  "data-test-id":
                                    "profile-information-address-state-field",
                                }}
                                label={
                                  <Translate
                                    translationKey={
                                      translationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_STATE
                                    }
                                  />
                                }
                              />
                            )}
                          />
                        </WhmGrid>
                      )}
                    </WhmGrid>
                    <WhmDivider />
                    <WhmStack>
                      <WhmTypography variant="subtitle1">
                        <Translate
                          translationKey={
                            translationKeys.PROFILE_KEY_PARTIES_ID_DOCUMENT_TITLE
                          }
                        />
                      </WhmTypography>
                      <WhmTypography variant="body2" color="text.secondary">
                        <TranslateHtml
                          translationKey={
                            translationKeys.PROFILE_KEY_PARTIES_ID_DOCUMENT_SUBTITLE
                          }
                        />
                      </WhmTypography>
                    </WhmStack>
                    <WhmGrid
                      container
                      columns={{ xs: 6, md: 12 }}
                      spacing="16px"
                    >
                      <WhmGrid item xs={6}>
                        <DatePickerInput
                          label="Birth Date"
                          dataTestId="key-party-birth-date"
                          disabled={readOnly || formik.isSubmitting}
                          language={language}
                          name="keyParty.birthDate"
                          onChange={(e) => {
                            void formik.setFieldValue(
                              "keyParty.birthDate",
                              e.target.value?.format("YYYY-MM-DD") ?? null,
                            );
                          }}
                          onBlur={formik.handleBlur}
                          value={
                            formik.values.keyParty.birthDate
                              ? moment.utc(formik.values.keyParty.birthDate)
                              : moment.invalid()
                          }
                          sx={{
                            width: "100%",
                            ".wh-icon-calendar": {
                              color: whmTheme.palette.action.active,
                            },
                          }}
                          error={
                            formik.touched.keyParty?.birthDate &&
                            Boolean(formik.errors.keyParty?.birthDate)
                          }
                          helperText={
                            (formik.touched.keyParty?.birthDate &&
                              formik.errors.keyParty?.birthDate) ||
                            getTranslation(
                              translationKeys.PROFILE_KEY_PARTIES_BIRTH_DATE_FORMAT,
                            )
                          }
                        />
                      </WhmGrid>
                      <WhmGrid item xs={6}>
                        <FormikWhmTextField
                          select
                          sx={{ margin: 0 }}
                          label="ID Document Type"
                          fullWidth
                          name="idType"
                          disabled={
                            readOnly ||
                            formik.isSubmitting ||
                            formik.values.keyParty.address.countryCode !== "US"
                          }
                          helperText={
                            formik.values.keyParty.address.countryCode !== "US"
                              ? getTranslation(
                                  translationKeys.PROFILE_KEY_PARTIES_PASSPORT_ONLY_NOTICE,
                                )
                              : ""
                          }
                        >
                          <WhmMenuItem value={KEY_PARTY_ID_TYPES.PASSPORT}>
                            <Translate
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_PASSPORT
                              }
                            />
                          </WhmMenuItem>
                          {formik.values.keyParty.address.countryCode ===
                            "US" && (
                            <WhmMenuItem
                              value={KEY_PARTY_ID_TYPES.DRIVERS_LICENSE}
                            >
                              <Translate
                                translationKey={
                                  translationKeys.PROFILE_KEY_PARTIES_DRIVERS_LICENSE
                                }
                              />
                            </WhmMenuItem>
                          )}
                        </FormikWhmTextField>
                      </WhmGrid>
                    </WhmGrid>

                    {formik.values.idType === KEY_PARTY_ID_TYPES.PASSPORT && (
                      <>
                        <WhmStack>
                          <WhmTypography>
                            <Translate
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_PASSPORT
                              }
                            />
                          </WhmTypography>
                          <WhmTypography variant="body2" color="text.secondary">
                            <TranslateHtml
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_PASSPORT_UPLOAD_INSTRUCTIONS
                              }
                            />
                          </WhmTypography>
                        </WhmStack>
                        <WhmGrid
                          container
                          columns={{ xs: 6, md: 12 }}
                          spacing="16px"
                        >
                          <WhmGrid item xs={6} md={12}>
                            <WhmDropzone
                              disabled={readOnly}
                              preview={
                                documents.find(
                                  (d) =>
                                    d.documentType ===
                                    KEY_PARTY_UPLOADER_KEYS.PASSPORT,
                                )?.fileName
                                  ? {
                                      url:
                                        documents.find(
                                          (d) =>
                                            d.documentType ===
                                            KEY_PARTY_UPLOADER_KEYS.PASSPORT,
                                        )?.fileUrl ?? "",
                                      fileName:
                                        documents.find(
                                          (d) =>
                                            d.documentType ===
                                            KEY_PARTY_UPLOADER_KEYS.PASSPORT,
                                        )?.fileName ?? "",
                                    }
                                  : undefined
                              }
                              onUpload={async (file: File) => {
                                await uploadDocument(
                                  file,
                                  KEY_PARTY_UPLOADER_KEYS.PASSPORT,
                                  documents.findIndex(
                                    (d) =>
                                      d.documentType ===
                                      KEY_PARTY_UPLOADER_KEYS.PASSPORT,
                                  ),
                                );
                              }}
                              onDelete={async () => {
                                const doc = documents.find(
                                  (d) =>
                                    d.documentType ===
                                    KEY_PARTY_UPLOADER_KEYS.PASSPORT,
                                );
                                await removeDocument(
                                  KEY_PARTY_UPLOADER_KEYS.PASSPORT,
                                  doc!.index,
                                  doc!.documentId,
                                );
                              }}
                              onValidate={(file: File) => {
                                if (file.size > 50 * 1024 * 1024) {
                                  return {
                                    error: getTranslation(
                                      translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_INVALID_SIZE_ERROR,
                                    ),
                                  };
                                }
                                return undefined;
                              }}
                              extraActionButtonText={
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_VIEW_SAMPLE
                                  }
                                />
                              }
                              onExtraActionClick={() =>
                                setPassportViewSampleDialogOpen(true)
                              }
                            />
                          </WhmGrid>
                        </WhmGrid>
                      </>
                    )}
                    {formik.values.idType ===
                      KEY_PARTY_ID_TYPES.DRIVERS_LICENSE && (
                      <>
                        <WhmStack>
                          <WhmTypography>
                            <Translate
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_DRIVERS_LICENSE
                              }
                            />
                          </WhmTypography>
                          <WhmTypography variant="body2" color="text.secondary">
                            <TranslateHtml
                              translationKey={
                                translationKeys.PROFILE_KEY_PARTIES_DRIVERS_LICENSE_UPLOAD_INSTRUCTIONS
                              }
                            />
                          </WhmTypography>
                        </WhmStack>
                        <WhmGrid
                          container
                          columns={{ xs: 6, md: 12 }}
                          spacing="16px"
                        >
                          <WhmGrid item xs={6}>
                            <WhmDropzone
                              disabled={readOnly}
                              preview={
                                documents.find(
                                  (d) =>
                                    d.documentType ===
                                    KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_FRONT,
                                )?.fileName
                                  ? {
                                      url:
                                        documents.find(
                                          (d) =>
                                            d.documentType ===
                                            KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_FRONT,
                                        )?.fileUrl ?? "",
                                      fileName:
                                        documents.find(
                                          (d) =>
                                            d.documentType ===
                                            KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_FRONT,
                                        )?.fileName ?? "",
                                    }
                                  : undefined
                              }
                              onUpload={async (file: File) => {
                                await uploadDocument(
                                  file,
                                  KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_FRONT,
                                  documents.findIndex(
                                    (d) =>
                                      d.documentType ===
                                      KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_FRONT,
                                  ),
                                );
                              }}
                              onDelete={async () => {
                                const doc = documents.find(
                                  (d) =>
                                    d.documentType ===
                                    KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_FRONT,
                                );
                                await removeDocument(
                                  KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_FRONT,
                                  doc!.index,
                                  doc!.documentId,
                                );
                              }}
                              extraActionButtonText={
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_VIEW_SAMPLE
                                  }
                                />
                              }
                              onExtraActionClick={() =>
                                setDriverLicenseFrontViewSampleDialogOpen(true)
                              }
                            />
                          </WhmGrid>
                          <WhmGrid item xs={6}>
                            <WhmDropzone
                              disabled={readOnly}
                              preview={
                                documents.find(
                                  (d) =>
                                    d.documentType ===
                                    KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_BACK,
                                )?.fileName
                                  ? {
                                      url:
                                        documents.find(
                                          (d) =>
                                            d.documentType ===
                                            KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_BACK,
                                        )?.fileUrl ?? "",
                                      fileName:
                                        documents.find(
                                          (d) =>
                                            d.documentType ===
                                            KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_BACK,
                                        )?.fileName ?? "",
                                    }
                                  : undefined
                              }
                              onUpload={async (file: File) => {
                                await uploadDocument(
                                  file,
                                  KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_BACK,
                                  documents.findIndex(
                                    (d) =>
                                      d.documentType ===
                                      KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_BACK,
                                  ),
                                );
                              }}
                              onDelete={async () => {
                                const doc = documents.find(
                                  (d) =>
                                    d.documentType ===
                                    KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_BACK,
                                );
                                await removeDocument(
                                  KEY_PARTY_UPLOADER_KEYS.DRIVERS_LICENSE_BACK,
                                  doc!.index,
                                  doc!.documentId,
                                );
                              }}
                              extraActionButtonText={
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_VIEW_SAMPLE
                                  }
                                />
                              }
                              onExtraActionClick={() =>
                                setDriverLicenseBackViewSampleDialogOpen(true)
                              }
                            />
                          </WhmGrid>
                        </WhmGrid>
                      </>
                    )}
                  </WhmStack>
                </Form>
              </WhmDialogContent>
              {displayBottomDivider && <WhmDivider />}
              <WhmDialogActions>
                <WhmButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    formik.resetForm();
                    handleClose();
                  }}
                >
                  <Translate
                    translationKey={translationKeys.PROFILE_KEY_PARTIES_CANCEL}
                  />
                </WhmButton>
                {!readOnly && (
                  <WhmLoadingButton
                    variant="contained"
                    type="submit"
                    form={addIndividualFormId}
                    disabled={
                      formik.isSubmitting ||
                      keyPartyDocumentsQuery.isPending ||
                      uploadInvestorDocumentTempMutation.isPending
                    }
                    loading={formik.isSubmitting}
                    sx={{
                      svg: {
                        display: "block !important",
                      },
                      "&.MuiLoadingButton-loading": {
                        color: "black",
                        opacity: "0.3",
                        span: {
                          color: "black",
                        },
                      },
                    }}
                  >
                    {formik.isSubmitting ? (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_UPDATE_INDIVIDUAL_SUBMIT_IN_PROGRESS
                        }
                      />
                    ) : (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_UPDATE_INDIVIDUAL_SUBMIT
                        }
                      />
                    )}
                  </WhmLoadingButton>
                )}
              </WhmDialogActions>
            </>
          )}
        </Formik>
        <PassportViewSampleDialog
          open={passportViewSampleDialogOpen}
          onClose={() => setPassportViewSampleDialogOpen(false)}
        />
        <DriversLicenseFrontViewSampleDialog
          open={driverLicenseFrontViewSampleDialogOpen}
          onClose={() => setDriverLicenseFrontViewSampleDialogOpen(false)}
        />
        <DriversLicenseBackViewSampleDialog
          open={driverLicenseBackViewSampleDialogOpen}
          onClose={() => setDriverLicenseBackViewSampleDialogOpen(false)}
        />
      </>
    </WhmDialog>
  );
}
